// 쓰다 만 글(초안).
//
// ## 이 기기가 먼저, 서버는 잇는 길
// 글자는 먼저 이 브라우저에 적힌다 — 네트워크가 없어도 사라지지 않게. 서버(`/v1/drafts`)로는
// `draft_sync.ts` 가 조금 늦게(디바운스) 올려 내 다른 기기와 잇는다. 그래서 칸마다 **고친 시각**과
// **서버와 맞춘 적 있는가**를 곁에 적는다(`<열쇠>.meta`). 둘이 다투면 늦게 고친 쪽이 이긴다.
//
// ## 빈 글은 지운다
// 비운 것은 "빈 초안" 이 아니라 초안 없음이다. 남겨 두면 사이드바에 영영 연필이
// 붙어 있게 된다.

import type { ChannelId, Seq } from '@maru/protocol'

/** 저장 열쇠. 로그인한 사람이 바뀌면 부르는 쪽에서 꼬리를 붙인다. */
export const DRAFT_KEY = 'maru.drafts'

/** 한 채널 초안의 최대 길이. 넘치면 앞에서부터 자른다. */
export const DRAFT_MAX = 8000

/**
 * 초안의 자리. 채널 입력칸은 채널 번호, 스레드 답글 칸은 `채널:뿌리` 다. 답글 칸의 글도 판을 닫았다
 * 다시 열면 남아 있어야 한다 — 닫는 순간 사라지면 쓰다 만 답이 날아간다(3차 F167).
 */
export type DraftKey = ChannelId | `${number}:${number}`

/** 스레드 답글 칸의 자리. */
export const thread_draft = (channel: ChannelId, root: Seq): DraftKey => `${channel}:${root}`

/** 자리 → 쓰다 만 글. */
export type Drafts = ReadonlyMap<DraftKey, string>

/** 우리가 쓰는 만큼의 저장소. `localStorage` 가 이 모양이다. */
export interface Store {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * 저장해 둔 글자를 초안으로 읽는다.
 *
 * 남이 망가뜨렸거나 예전 판일 수 있으니 **한 줄씩** 믿는다 — 한 칸이 이상하다고
 * 나머지 초안까지 버리면 사람 글이 사라진다.
 */
export function read_drafts(raw: string | null): Drafts {
  const out = new Map<DraftKey, string>()
  if (raw === null) return out
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return out
  }
  if (typeof parsed !== 'object' || parsed === null) return out
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value !== 'string' || value.trim() === '') continue
    if (/^[1-9]\d*:[1-9]\d*$/.test(key)) {
      out.set(key as DraftKey, value.slice(0, DRAFT_MAX))
      continue
    }
    const id = Number(key)
    if (!Number.isSafeInteger(id) || id <= 0) continue
    out.set(id as ChannelId, value.slice(0, DRAFT_MAX))
  }
  return out
}

/** 초안을 글자로 되돌린다. */
export function write_drafts(drafts: Drafts): string {
  return JSON.stringify(Object.fromEntries([...drafts].map(([id, text]) => [String(id), text])))
}

/** 한 자리의 초안을 갈아 끼운 **새** 지도. 빈 글이면 지운다. */
export function put_draft(drafts: Drafts, channel: DraftKey, text: string): Drafts {
  const next = new Map(drafts)
  if (text.trim() === '') next.delete(channel)
  else next.set(channel, text.slice(0, DRAFT_MAX))
  return next
}

/** 칸 하나의 곁 기록. `at` 은 고친 시각(ms), `synced` 는 서버와 맞춘 적이 있는가. */
export interface Stamp {
  at: number
  synced: boolean
}

function read_stamps(raw: string | null): Map<DraftKey, Stamp> {
  const out = new Map<DraftKey, Stamp>()
  if (raw === null) return out
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return out
  }
  if (typeof parsed !== 'object' || parsed === null) return out
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue
    const { at, synced } = value as { at?: unknown; synced?: unknown }
    if (typeof at !== 'number' || !Number.isFinite(at)) continue
    const k = /^\d+$/.test(key) ? (Number(key) as ChannelId) : (key as DraftKey)
    out.set(k, { at, synced: synced === true })
  }
  return out
}

/**
 * 저장소에 붙은 초안첩.
 *
 * 저장은 던질 수 있다(사파리 비공개 모드, 용량 초과). 초안은 편의지 약속이
 * 아니므로, 못 적으면 조용히 넘어가고 화면은 계속 돈다.
 */
export class DraftBook {
  private drafts: Drafts
  private stamps: Map<DraftKey, Stamp>
  private readonly store: Store
  private readonly key: string
  constructor(store: Store, key: string = DRAFT_KEY) {
    this.store = store
    this.key = key
    this.drafts = read_drafts(safe_get(store, key))
    this.stamps = read_stamps(safe_get(store, `${key}.meta`))
  }

  /** 칸의 곁 기록. 예전 판에서 온 칸은 기록이 없다 — 시각 0, 안 맞춤으로 본다. */
  stamp(channel: DraftKey): Stamp {
    return this.stamps.get(channel) ?? { at: 0, synced: false }
  }

  /** 서버와 맞췄다고 적는다(올렸거나 받았다). */
  mark_synced(channel: DraftKey): void {
    const was = this.stamps.get(channel)
    if (was === undefined || !this.drafts.has(channel)) return
    this.stamps.set(channel, { ...was, synced: true })
    this.save_stamps()
  }

  private save_stamps(): void {
    try {
      this.store.setItem(
        `${this.key}.meta`,
        JSON.stringify(Object.fromEntries([...this.stamps].map(([k, v]) => [String(k), v]))),
      )
    } catch {
      // 곁 기록도 편의다.
    }
  }

  all(): Drafts {
    return this.drafts
  }

  /** 초안이 있는 **채널**들 — 사이드바의 연필. 스레드 답글 칸의 초안은 세지 않는다. */
  channels(): Set<ChannelId> {
    return new Set([...this.drafts.keys()].filter((k): k is ChannelId => typeof k === 'number'))
  }

  of(channel: DraftKey): string {
    return this.drafts.get(channel) ?? ''
  }

  /** 갈아 끼운다. `at` 은 고친 시각, `synced` 는 서버에서 받은 글인가. */
  put(channel: DraftKey, text: string, at: number = Date.now(), synced = false): Drafts {
    this.drafts = put_draft(this.drafts, channel, text)
    if (this.drafts.has(channel)) this.stamps.set(channel, { at, synced })
    else this.stamps.delete(channel)
    try {
      this.store.setItem(this.key, write_drafts(this.drafts))
    } catch {
      // 못 적었을 뿐이다. 이 판이 열려 있는 동안은 기억이 남는다.
    }
    this.save_stamps()
    return this.drafts
  }
}

function safe_get(store: Store, key: string): string | null {
  try {
    return store.getItem(key)
  } catch {
    return null
  }
}
